import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Bell, Mail, Gift, Trophy, Users, TrendingUp,
  Megaphone, Loader2, Save, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationPreferences = () => {
  const { token, user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isSponsor = user?.role === 'sponsor' || user?.role === 'admin';

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await fetch(`${API}/api/users/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch preferences');
        
        const data = await response.json();
        setPreferences(data);
      } catch (error) {
        toast.error('Failed to load notification preferences');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [token]);

  const handleToggle = async (key, value) => {
    try {
      setSaving(true);
      setSaved(false);
      
      const response = await fetch(`${API}/api/users/notification-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (!response.ok) throw new Error('Failed to update preference');

      const data = await response.json();
      setPreferences(data);
      setSaved(true);
      
      // Reset saved indicator after 2 seconds
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  const notificationSettings = [
    {
      key: 'email_new_giveaways',
      icon: Gift,
      title: 'New Giveaways',
      description: 'Get notified when new giveaways are available in your area',
      enabled: preferences?.email_new_giveaways ?? true,
      show: true
    },
    {
      key: 'email_entry_confirmation',
      icon: CheckCircle,
      title: 'Entry Confirmations',
      description: 'Receive confirmation when you enter a giveaway',
      enabled: preferences?.email_entry_confirmation ?? true,
      show: true
    },
    {
      key: 'email_giveaway_ending',
      icon: Bell,
      title: 'Giveaway Ending Reminders',
      description: 'Get reminded when giveaways you entered are ending soon',
      enabled: preferences?.email_giveaway_ending ?? true,
      show: true
    },
    {
      key: 'email_winner_announcement',
      icon: Trophy,
      title: 'Winner Announcements',
      description: 'Be notified about giveaway results and winner announcements',
      enabled: preferences?.email_winner_announcement ?? true,
      show: true
    },
    {
      key: 'email_new_leads',
      icon: Users,
      title: 'New Lead Alerts',
      description: 'Get notified immediately when you receive new leads',
      enabled: preferences?.email_new_leads ?? true,
      show: isSponsor
    },
    {
      key: 'email_marketing',
      icon: Megaphone,
      title: 'Marketing & Tips',
      description: 'Receive tips, best practices, and promotional content',
      enabled: preferences?.email_marketing ?? false,
      show: true
    }
  ];

  return (
    <div className="max-w-2xl space-y-6" data-testid="notification-preferences-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-[#FF3B30]" />
            Notification Preferences
          </h1>
          <p className="text-neutral-500 mt-1">
            Manage how and when you receive notifications
          </p>
        </div>
        {saving && (
          <div className="flex items-center gap-2 text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Saving...</span>
          </div>
        )}
        {saved && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Saved</span>
          </div>
        )}
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-neutral-600" />
            <h2 className="font-semibold text-neutral-900">Email Notifications</h2>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Choose which emails you want to receive
          </p>
        </div>
        
        <div className="divide-y divide-neutral-100">
          {notificationSettings.filter(s => s.show).map((setting) => (
            <div 
              key={setting.key} 
              className="px-6 py-4 flex items-center justify-between"
              data-testid={`notification-setting-${setting.key}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  setting.enabled ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-neutral-100 text-neutral-400'
                }`}>
                  <setting.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900">{setting.title}</p>
                  <p className="text-sm text-neutral-500">{setting.description}</p>
                </div>
              </div>
              <Switch
                checked={setting.enabled}
                onCheckedChange={(checked) => handleToggle(setting.key, checked)}
                data-testid={`toggle-${setting.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Push Notifications (Future) */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden opacity-60">
        <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-neutral-600" />
            <h2 className="font-semibold text-neutral-900">Push Notifications</h2>
            <span className="px-2 py-0.5 bg-neutral-200 text-neutral-600 text-xs rounded-full">
              Coming Soon
            </span>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Get instant notifications on your device
          </p>
        </div>
        
        <div className="px-6 py-8 text-center text-neutral-500">
          <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>Push notifications will be available soon</p>
        </div>
      </div>

      {/* Email Preview */}
      <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-xl p-6 text-white">
        <h3 className="font-semibold mb-2">Email Delivery</h3>
        <p className="text-neutral-400 text-sm mb-4">
          Notifications are sent to: <span className="text-white">{user?.email}</span>
        </p>
        <p className="text-neutral-400 text-sm">
          You can unsubscribe from any email by clicking the unsubscribe link at the bottom of each message.
        </p>
      </div>
    </div>
  );
};

export default NotificationPreferences;
